// src/components/Livreurs.js
import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TablePagination,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add,
  Search,
  Save,
  Cancel,
  Edit,
  Delete,
  Tag,
  Person,
  Phone,
  Email,
  LocationCity,
  ToggleOn,
  CheckCircle,
  Store,
  Settings,
  Info,
  ReceiptLong,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom'; // Added useLocation
import { AuthContext } from '../context/AuthContext';
import debounce from 'lodash.debounce';

// Styled components (aligned with Restaurants.js)
const SectionTitle = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2),
  fontWeight: 600,
  color: '#1e293b',
  fontSize: '1.6rem',
  lineHeight: 1.3,
  position: 'relative',
  '&:before': {
    content: '""',
    position: 'absolute',
    left: -16,
    top: 0,
    height: '100%',
    width: 4,
    background: 'linear-gradient(to bottom, #21ccbb, #0d9488)',
  },
  fontFamily: "Inter, sans-serif",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    borderRadius: 12,
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(15, 23, 42, 0.1)',
    transition: 'all 0.3s ease',
    fontFamily: "Inter, sans-serif",
    '&:hover': {
      borderColor: '#21ccbb',
      boxShadow: '0 4px 12px rgba(33, 204, 187, 0.2)',
    },
    '&.Mui-focused': {
      borderColor: '#21ccbb',
      boxShadow: '0 4px 16px rgba(33, 204, 187, 0.3)',
    },
  },
  '& .MuiInputBase-input': {
    padding: '14px 16px',
    fontSize: '0.95rem',
    color: '#1e293b',
    fontFamily: "Inter, sans-serif",
  },
  '& .MuiInputLabel-root': {
    color: '#64748b',
    fontWeight: 500,
    fontSize: '0.95rem',
    fontFamily: "Inter, sans-serif",
    '&.Mui-focused': {
      color: '#21ccbb',
    },
  },
  '& .MuiFormHelperText-root': {
    color: '#f43f5e',
    fontSize: '0.85rem',
    fontFamily: "Inter, sans-serif",
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: 12,
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(8px)',
  border: '1px solid rgba(15, 23, 42, 0.1)',
  transition: 'all 0.3s ease',
  fontFamily: "Inter, sans-serif",
  '& .MuiSelect-select': {
    padding: '16px 20px',
    fontSize: '1rem',
    color: '#1e293b',
    fontFamily: "Inter, sans-serif",
    display: 'flex',
    alignItems: 'center',
  },
  '&:hover': {
    borderColor: '#21ccbb',
    boxShadow: '0 4px 12px rgba(33, 204, 187, 0.2)',
  },
  '&.Mui-focused': {
    borderColor: '#21ccbb',
    boxShadow: '0 4px 16px rgba(33, 204, 187, 0.3)',
  },
  height: '56px',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  fontWeight: 600,
  borderRadius: 12,
  background: 'linear-gradient(45deg, #21ccbb 30%, #0d9488 90%)',
  color: '#fff',
  textTransform: 'none',
  fontSize: '0.95rem',
  fontFamily: "Inter, sans-serif",
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 12px rgba(33, 204, 187, 0.3)',
  '&:hover': {
    background: 'linear-gradient(45deg, #0d9488 30%, #21ccbb 90%)',
    boxShadow: '0 8px 24px rgba(33, 204, 187, 0.5)',
    transform: 'scale(1.05)',
  },
  '&:disabled': {
    background: '#e2e8f0',
    color: '#94a3b8',
    boxShadow: 'none',
  },
}));

const StyledCancelButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  fontWeight: 600,
  borderRadius: 12,
  background: 'linear-gradient(45deg, #64748b 30%, #475569 90%)',
  color: '#fff',
  textTransform: 'none',
  fontSize: '0.95rem',
  fontFamily: "Inter, sans-serif",
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 12px rgba(100, 116, 139, 0.3)',
  '&:hover': {
    background: 'linear-gradient(45deg, #475569 30%, #64748b 90%)',
    boxShadow: '0 8px 24px rgba(100, 116, 139, 0.5)',
    transform: 'scale(1.05)',
  },
  '&:disabled': {
    background: '#e2e8f0',
    color: '#94a3b8',
    boxShadow: 'none',
  },
}));

const StyledDeleteButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  fontWeight: 600,
  borderRadius: 12,
  background: 'linear-gradient(45deg, #f43f5e 30%, #e11d48 90%)',
  color: '#fff',
  textTransform: 'none',
  fontSize: '0.95rem',
  fontFamily: "Inter, sans-serif",
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 12px rgba(244, 63, 94, 0.3)',
  '&:hover': {
    background: 'linear-gradient(45deg, #e11d48 30%, #f43f5e 90%)',
    boxShadow: '0 8px 24px rgba(244, 63, 94, 0.5)',
    transform: 'scale(1.05)',
  },
  '&:disabled': {
    background: '#e2e8f0',
    color: '#94a3b8',
    boxShadow: 'none',
  },
}));

// Tab Panel Component
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const Livreurs = React.memo(() => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation(); // Added to detect route changes
  const [livreurs, setLivreurs] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 0, limit: 10 });
  const [search, setSearch] = useState('');
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingLivreur, setEditingLivreur] = useState(null); // Track livreur being edited
  const [formData, setFormData] = useState({
    nom: '',
    telephone: '',
    email: '',
    ville: '',
    ice: '',
    rib: '',
    cin: '',
    adresse: '',
    equipement: 0,
    equipement_active: 0,
    etat: 1,
    active: 0,
  });
  const [factures, setFactures] = useState([]); // State for invoices
  const [facturesLoading, setFacturesLoading] = useState(false); // Loading state for invoices
  const [tabValue, setTabValue] = useState(0); // State for tabs
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  // Check if user has permission
  const hasPermission = user && ['admin', 'manager'].includes(user.role);

  // Reset editingLivreur when navigating to /livreurs
  useEffect(() => {
    if (location.pathname === '/livreurs') {
      setEditingLivreur(null); // Reset edit mode to show the list view
      setFactures([]); // Clear factures
      setTabValue(0); // Reset tab to "Informations"
    }
  }, [location.pathname]);

  // Debounced search handler for livreurs
  const debouncedFetchLivreurs = useMemo(
    () =>
      debounce(async (page, limit, searchTerm) => {
        if (!hasPermission) {
          setError('Vous n\'avez pas la permission de voir les livreurs. Veuillez vous connecter en tant qu\'administrateur ou gestionnaire.');
          return;
        }

        setSearchLoading(true);
        setError(null);
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get('https://apibackoffice.lalivraison.ma/api/livreurs', {
            params: { page, limit, search: searchTerm },
            headers: { Authorization: `Bearer ${token}` },
          });
          setLivreurs(response.data.livreurs);
          setPagination(response.data.pagination);
        } catch (err) {
          console.error('Fetch error:', err.response?.data || err.message);
          if (err.response?.status === 403) {
            setError('Vous n\'avez pas la permission de voir les livreurs. Veuillez vérifier que vous êtes connecté en tant qu\'administrateur ou gestionnaire.');
          } else if (err.response?.status === 401) {
            setError('Votre session a expiré. Veuillez vous reconnecter.');
            localStorage.removeItem('token');
            navigate('/login');
          } else {
            setError(`Échec de la récupération des livreurs : ${err.response?.data?.message || err.message}`);
          }
        } finally {
          setSearchLoading(false);
          setLoading(false);
        }
      }, 500),
    [hasPermission, navigate]
  );

  // Fetch livreurs
  const fetchLivreurs = useCallback(
    (page = 1, limit = 10, search = '') => {
      setLoading(true);
      debouncedFetchLivreurs(page, limit, search);
    },
    [debouncedFetchLivreurs]
  );

  // Fetch factures for a livreur
  const fetchFactures = useCallback(async (livreurId) => {
    if (!hasPermission) {
      setError('Vous n\'avez pas la permission de voir les factures. Veuillez vous connecter en tant qu\'administrateur ou gestionnaire.');
      return;
    }

    setFacturesLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`https://apibackoffice.lalivraison.ma/api/livreurs/${livreurId}/factures`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFactures(response.data.factures || []);
    } catch (err) {
      console.error('Fetch factures error:', err.response?.data || err.message);
      if (err.response?.status === 403) {
        setError('Vous n\'avez pas la permission de voir les factures. Veuillez vérifier que vous êtes connecté en tant qu\'administrateur ou gestionnaire.');
      } else if (err.response?.status === 401) {
        setError('Votre session a expiré. Veuillez vous reconnecter.');
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        setError(`Échec de la récupération des factures : ${err.response?.data?.message || err.message}`);
      }
    } finally {
      setFacturesLoading(false);
    }
  }, [hasPermission, navigate]);

  // Initial fetch for livreurs
  useEffect(() => {
    if (hasPermission) {
      fetchLivreurs(1, pagination.limit, search);
    }
    return () => {
      debouncedFetchLivreurs.cancel();
    };
  }, [fetchLivreurs, pagination.limit, search, hasPermission]);

  // Fetch factures when editingLivreur changes
  useEffect(() => {
    if (editingLivreur) {
      fetchFactures(editingLivreur.id_livreur);
    }
  }, [editingLivreur, fetchFactures]);

  // Handle search input
  const handleSearch = useCallback((e) => {
    setSearch(e.target.value);
  }, []);

  // Handle pagination
  const handleChangePage = useCallback(
    (event, newPage) => {
      fetchLivreurs(newPage + 1, pagination.limit, search);
    },
    [fetchLivreurs, pagination.limit, search]
  );

  const handleChangeRowsPerPage = useCallback(
    (event) => {
      const newLimit = parseInt(event.target.value, 10);
      setPagination((prev) => ({ ...prev, limit: newLimit, page: 0 }));
      fetchLivreurs(1, newLimit, search);
    },
    [fetchLivreurs, search]
  );

  // Open create dialog
  const handleOpenCreateDialog = useCallback(() => {
    if (!hasPermission) {
      setError('Vous n\'avez pas la permission de créer des livreurs. Veuillez vous connecter en tant qu\'administrateur ou gestionnaire.');
      return;
    }
    setFormData({
      nom: '',
      telephone: '',
      email: '',
      ville: '',
      ice: '',
      rib: '',
      cin: '',
      adresse: '',
      equipement: 0,
      equipement_active: 0,
      etat: 1,
      active: 0,
    });
    setOpenCreateDialog(true);
  }, [hasPermission]);

  // Start editing a livreur
  const handleEdit = useCallback(
    (livreur) => {
      if (!hasPermission) {
        setError('Vous n\'avez pas la permission de modifier les livreurs. Veuillez vous connecter en tant qu\'administrateur ou gestionnaire.');
        return;
      }
      setEditingLivreur(livreur);
      setTabValue(0); // Reset to the first tab
      setFormData({
        nom: livreur.nom,
        telephone: livreur.telephone,
        email: livreur.email || '',
        ville: livreur.ville || '',
        ice: livreur.ice || '',
        rib: livreur.rib || '',
        cin: livreur.cin || '',
        adresse: livreur.adresse || '',
        equipement: livreur.equipement,
        equipement_active: livreur.equipement_active,
        etat: livreur.etat,
        active: livreur.active,
      });
    },
    [hasPermission]
  );

  // Cancel editing
  const handleCancelEdit = useCallback(() => {
    setEditingLivreur(null);
    setFactures([]); // Clear factures
    setTabValue(0); // Reset tab
    setFormData({
      nom: '',
      telephone: '',
      email: '',
      ville: '',
      ice: '',
      rib: '',
      cin: '',
      adresse: '',
      equipement: 0,
      equipement_active: 0,
      etat: 1,
      active: 0,
    });
  }, []);

  // Handle form input changes
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Handle tab change
  const handleTabChange = useCallback((event, newValue) => {
    setTabValue(newValue);
  }, []);

  // Submit create form
  const handleCreateSubmit = useCallback(async () => {
    if (!hasPermission) {
      setError('Vous n\'avez pas la permission de créer des livreurs. Veuillez vous connecter en tant qu\'administrateur ou gestionnaire.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const token = localStorage.getItem('token');
      await axios.post('https://apibackoffice.lalivraison.ma/api/livreurs', formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Livreur créé avec succès !');
      setOpenCreateDialog(false);
      fetchLivreurs(pagination.page + 1, pagination.limit, search);
    } catch (err) {
      console.error('Create error:', err.response?.data || err.message);
      if (err.response?.status === 403) {
        setError('Vous n\'avez pas la permission d\'effectuer cette action. Veuillez vérifier que vous êtes connecté en tant qu\'administrateur ou gestionnaire.');
      } else if (err.response?.status === 401) {
        setError('Votre session a expiré. Veuillez vous reconnecter.');
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        setError(`Échec de la création du livreur : ${err.response?.data?.message || err.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [hasPermission, formData, fetchLivreurs, pagination.page, pagination.limit, search, navigate]);

  // Submit edit form
  const handleEditSubmit = useCallback(async () => {
    if (!hasPermission) {
      setError('Vous n\'avez pas la permission de modifier les livreurs. Veuillez vous connecter en tant qu\'administrateur ou gestionnaire.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`https://apibackoffice.lalivraison.ma/api/livreurs/${editingLivreur.id_livreur}`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Livreur mis à jour avec succès !');
      setEditingLivreur(null);
      fetchLivreurs(pagination.page + 1, pagination.limit, search);
    } catch (err) {
      console.error('Edit error:', err.response?.data || err.message);
      if (err.response?.status === 403) {
        setError('Vous n\'avez pas la permission d\'effectuer cette action. Veuillez vérifier que vous êtes connecté en tant qu\'administrateur ou gestionnaire.');
      } else if (err.response?.status === 401) {
        setError('Votre session a expiré. Veuillez vous reconnecter.');
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        setError(`Échec de la mise à jour du livreur : ${err.response?.data?.message || err.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [hasPermission, editingLivreur, formData, fetchLivreurs, pagination.page, pagination.limit, search, navigate]);

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = useCallback(
    (id) => {
      if (!hasPermission) {
        setError('Vous n\'avez pas la permission de supprimer les livreurs. Veuillez vous connecter en tant qu\'administrateur ou gestionnaire.');
        return;
      }
      setDeleteId(id);
      setOpenDeleteDialog(true);
    },
    [hasPermission]
  );

  // Delete livreur
  const handleDelete = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`https://apibackoffice.lalivraison.ma/api/livreurs/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Livreur supprimé avec succès !');
      setOpenDeleteDialog(false);
      fetchLivreurs(pagination.page + 1, pagination.limit, search);
    } catch (err) {
      console.error('Delete error:', err.response?.data || err.message);
      if (err.response?.status === 403) {
        setError('Vous n\'avez pas la permission de supprimer les livreurs. Veuillez vérifier que vous êtes connecté en tant qu\'administrateur ou gestionnaire.');
      } else if (err.response?.status === 401) {
        setError('Votre session a expiré. Veuillez vous reconnecter.');
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        setError(`Échec de la suppression du livreur : ${err.response?.data?.message || err.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [deleteId, fetchLivreurs, pagination.page, pagination.limit, search, navigate]);

  // Handle Snackbar close
  const handleCloseSnackbar = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  // Redirect if no permission
  if (!hasPermission) {
    return (
      <Box sx={{ p: 3 }}>
        <SectionTitle variant="h4">Livreurs</SectionTitle>
        <Alert
          severity="error"
          sx={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(15, 23, 42, 0.1)', fontFamily: "Inter, sans-serif" }}
        >
          Vous n'avez pas la permission de voir les livreurs. Veuillez{' '}
          <Button
            onClick={() => navigate('/login')}
            sx={{ color: '#21ccbb', fontWeight: 600, textTransform: 'none', fontFamily: "Inter, sans-serif" }}
          >
            vous connecter en tant qu'administrateur ou gestionnaire
          </Button>
        </Alert>
      </Box>
    );
  }

  // Render edit form (Informations tab)
  const renderEditForm = () => (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
      <StyledTextField
        fullWidth
        label="Nom"
        name="nom"
        value={formData.nom}
        onChange={handleInputChange}
        margin="normal"
        required
      />
      <StyledTextField
        fullWidth
        label="Téléphone"
        name="telephone"
        value={formData.telephone}
        onChange={handleInputChange}
        margin="normal"
        required
      />
      <StyledTextField
        fullWidth
        label="Email"
        name="email"
        value={formData.email}
        onChange={handleInputChange}
        margin="normal"
        type="email"
      />
      <StyledTextField
        fullWidth
        label="Ville"
        name="ville"
        value={formData.ville}
        onChange={handleInputChange}
        margin="normal"
      />
      <StyledTextField
        fullWidth
        label="ICE"
        name="ice"
        value={formData.ice}
        onChange={handleInputChange}
        margin="normal"
      />
      <StyledTextField
        fullWidth
        label="RIB"
        name="rib"
        value={formData.rib}
        onChange={handleInputChange}
        margin="normal"
      />
      <StyledTextField
        fullWidth
        label="CIN"
        name="cin"
        value={formData.cin}
        onChange={handleInputChange}
        margin="normal"
      />
      <StyledTextField
        fullWidth
        label="Adresse"
        name="adresse"
        value={formData.adresse}
        onChange={handleInputChange}
        margin="normal"
      />
      <StyledTextField
        fullWidth
        label="Coût de l'équipement"
        name="equipement"
        type="number"
        value={formData.equipement}
        onChange={handleInputChange}
        margin="normal"
        inputProps={{ step: '0.01' }}
      />
      <FormControl fullWidth margin="normal">
        <InputLabel sx={{ fontFamily: "Inter, sans-serif" }}>Équipement Actif</InputLabel>
        <StyledSelect
          name="equipement_active"
          value={formData.equipement_active}
          onChange={handleInputChange}
        >
          <MenuItem value={0}>Non</MenuItem>
          <MenuItem value={1}>Oui</MenuItem>
        </StyledSelect>
      </FormControl>
      <FormControl fullWidth margin="normal">
        <InputLabel sx={{ fontFamily: "Inter, sans-serif" }}>Statut (État)</InputLabel>
        <StyledSelect
          name="etat"
          value={formData.etat}
          onChange={handleInputChange}
        >
          <MenuItem value={0}>Désactivé</MenuItem>
          <MenuItem value={1}>Activé</MenuItem>
        </StyledSelect>
      </FormControl>
      <FormControl fullWidth margin="normal">
        <InputLabel sx={{ fontFamily: "Inter, sans-serif" }}>Actif</InputLabel>
        <StyledSelect
          name="active"
          value={formData.active}
          onChange={handleInputChange}
        >
          <MenuItem value={0}>Non</MenuItem>
          <MenuItem value={1}>Oui</MenuItem>
        </StyledSelect>
      </FormControl>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <StyledCancelButton
          onClick={handleCancelEdit}
          disabled={loading}
          startIcon={<Cancel sx={{ color: '#fff' }} />}
        >
          Annuler
        </StyledCancelButton>
        <StyledButton
          onClick={handleEditSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <Save sx={{ color: '#fff' }} />}
        >
          Mettre à jour
        </StyledButton>
      </Box>
    </Box>
  );

  // Render factures tab
  const renderFactures = () => (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      {facturesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '20vh' }}>
          <CircularProgress size={40} sx={{ color: '#21ccbb' }} />
        </Box>
      ) : factures.length === 0 ? (
        <Typography sx={{ fontFamily: "Inter, sans-serif", color: '#64748b', textAlign: 'center', mt: 4 }}>
          Aucune facture trouvée pour ce livreur.
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 12, boxShadow: '0 4px 16px rgba(15, 23, 42, 0.1)' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tag sx={{ mr: 1, color: '#21ccbb', fontSize: '1rem' }} />
                    ID Facture
                  </Box>
                </TableCell>
                <TableCell sx={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ReceiptLong sx={{ mr: 1, color: '#21ccbb', fontSize: '1rem' }} />
                    Date
                  </Box>
                </TableCell>
                <TableCell sx={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ReceiptLong sx={{ mr: 1, color: '#21ccbb', fontSize: '1rem' }} />
                    Montant
                  </Box>
                </TableCell>
                <TableCell sx={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircle sx={{ mr: 1, color: '#21ccbb', fontSize: '1rem' }} />
                    Statut
                  </Box>
                </TableCell>
                <TableCell sx={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Store sx={{ mr: 1, color: '#21ccbb', fontSize: '1rem' }} />
                    Commande ID
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {factures.map((facture) => (
                <TableRow
                  key={facture.id_facture}
                  sx={{ '&:hover': { backgroundColor: 'rgba(33, 204, 187, 0.1)' } }}
                >
                  <TableCell sx={{ fontFamily: "Inter, sans-serif" }}>{facture.id_facture}</TableCell>
                  <TableCell sx={{ fontFamily: "Inter, sans-serif" }}>{facture.date}</TableCell>
                  <TableCell sx={{ fontFamily: "Inter, sans-serif" }}>{facture.montant} MAD</TableCell>
                  <TableCell sx={{ fontFamily: "Inter, sans-serif" }}>{facture.statut}</TableCell>
                  <TableCell sx={{ fontFamily: "Inter, sans-serif" }}>{facture.commande_id || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  // Render edit view with tabs
  const renderEditView = () => (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
      <SectionTitle variant="h4">Détails du Livreur : {editingLivreur.nom}</SectionTitle>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        sx={{
          borderBottom: '1px solid rgba(15, 23, 42, 0.1)',
          '& .MuiTab-root': {
            fontFamily: "Inter, sans-serif",
            textTransform: 'none',
            fontWeight: 500,
            color: '#64748b',
            '&:hover': { color: '#21ccbb' },
            '&.Mui-selected': { color: '#21ccbb' },
          },
          '& .MuiTabs-indicator': { backgroundColor: '#21ccbb' },
        }}
      >
        <Tab
          label="Informations"
          icon={<Info sx={{ color: '#21ccbb' }} />}
          iconPosition="start"
          sx={{ fontFamily: "Inter, sans-serif" }}
        />
        <Tab
          label="Factures"
          icon={<ReceiptLong sx={{ color: '#21ccbb' }} />}
          iconPosition="start"
          sx={{ fontFamily: "Inter, sans-serif" }}
        />
      </Tabs>
      <TabPanel value={tabValue} index={0}>
        {renderEditForm()}
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        {renderFactures()}
      </TabPanel>
    </Box>
  );

  // Render table view
  const renderTableView = () => (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <StyledTextField
          label="Rechercher par nom, téléphone ou email"
          variant="outlined"
          value={search}
          onChange={handleSearch}
          sx={{ width: '300px' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {searchLoading ? (
                  <CircularProgress size={20} sx={{ color: '#21ccbb' }} />
                ) : (
                  <Search sx={{ color: '#21ccbb' }} />
                )}
              </InputAdornment>
            ),
          }}
        />
        <StyledButton onClick={handleOpenCreateDialog} startIcon={<Add sx={{ color: '#fff' }} />}>
          Ajouter un Livreur
        </StyledButton>
      </Box>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 12, fontFamily: "Inter, sans-serif" }}
          onClose={handleCloseSnackbar}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2, borderRadius: 12, fontFamily: "Inter, sans-serif" }}
          onClose={handleCloseSnackbar}
        >
          {success}
        </Alert>
      )}
      {loading && !searchLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress size={40} sx={{ color: '#21ccbb' }} />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ borderRadius: 12, boxShadow: '0 4px 16px rgba(15, 23, 42, 0.1)' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Tag sx={{ mr: 1, color: '#21ccbb', fontSize: '1rem' }} />
                      ID
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Person sx={{ mr: 1, color: '#21ccbb', fontSize: '1rem' }} />
                      Nom
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Phone sx={{ mr: 1, color: '#21ccbb', fontSize: '1rem' }} />
                      Téléphone
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Email sx={{ mr: 1, color: '#21ccbb', fontSize: '1rem' }} />
                      Email
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocationCity sx={{ mr: 1, color: '#21ccbb', fontSize: '1rem' }} />
                      Ville
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ToggleOn sx={{ mr: 1, color: '#21ccbb', fontSize: '1rem' }} />
                      Statut
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircle sx={{ mr: 1, color: '#21ccbb', fontSize: '1rem' }} />
                      Actif
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Settings sx={{ mr: 1, color: '#21ccbb', fontSize: '1rem' }} />
                      Actions
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {livreurs.map((livreur) => (
                  <TableRow
                    key={livreur.id_livreur}
                    onClick={() => handleEdit(livreur)}
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(33, 204, 187, 0.1)' } }}
                  >
                    <TableCell sx={{ fontFamily: "Inter, sans-serif" }}>{livreur.id_livreur}</TableCell>
                    <TableCell sx={{ fontFamily: "Inter, sans-serif" }}>{livreur.nom}</TableCell>
                    <TableCell sx={{ fontFamily: "Inter, sans-serif" }}>{livreur.telephone}</TableCell>
                    <TableCell sx={{ fontFamily: "Inter, sans-serif" }}>{livreur.email || '-'}</TableCell>
                    <TableCell sx={{ fontFamily: "Inter, sans-serif" }}>{livreur.ville || '-'}</TableCell>
                    <TableCell sx={{ fontFamily: "Inter, sans-serif" }}>{livreur.etat ? 'Activé' : 'Désactivé'}</TableCell>
                    <TableCell sx={{ fontFamily: "Inter, sans-serif" }}>{livreur.active ? 'Oui' : 'Non'}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <IconButton onClick={() => handleEdit(livreur)} sx={{ color: '#21ccbb' }}>
                        <Edit sx={{ color: '#21ccbb' }} />
                      </IconButton>
                      <IconButton onClick={() => handleOpenDeleteDialog(livreur.id_livreur)} sx={{ color: '#f43f5e' }}>
                        <Delete sx={{ color: '#f43f5e' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={pagination.total}
            rowsPerPage={pagination.limit}
            page={pagination.page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ fontFamily: "Inter, sans-serif" }}
            labelRowsPerPage="Ligne par page:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </>
      )}
    </>
  );

  return (
    <Box sx={{ p: 3 }}>
      <SectionTitle variant="h4">Livreurs</SectionTitle>
      {editingLivreur ? renderEditView() : renderTableView()}

      {/* Create Dialog */}
      <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: "Inter, sans-serif" }}>Ajouter Livreur</DialogTitle>
        <DialogContent>
          <StyledTextField
            fullWidth
            label="Nom"
            name="nom"
            value={formData.nom}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <StyledTextField
            fullWidth
            label="Téléphone"
            name="telephone"
            value={formData.telephone}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <StyledTextField
            fullWidth
            label="Email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            margin="normal"
            type="email"
          />
          <StyledTextField
            fullWidth
            label="Ville"
            name="ville"
            value={formData.ville}
            onChange={handleInputChange}
            margin="normal"
          />
          <StyledTextField
            fullWidth
            label="ICE"
            name="ice"
            value={formData.ice}
            onChange={handleInputChange}
            margin="normal"
          />
          <StyledTextField
            fullWidth
            label="RIB"
            name="rib"
            value={formData.rib}
            onChange={handleInputChange}
            margin="normal"
          />
          <StyledTextField
            fullWidth
            label="CIN"
            name="cin"
            value={formData.cin}
            onChange={handleInputChange}
            margin="normal"
          />
          <StyledTextField
            fullWidth
            label="Adresse"
            name="adresse"
            value={formData.adresse}
            onChange={handleInputChange}
            margin="normal"
          />
          <StyledTextField
            fullWidth
            label="Coût de l'équipement"
            name="equipement"
            type="number"
            value={formData.equipement}
            onChange={handleInputChange}
            margin="normal"
            inputProps={{ step: '0.01' }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel sx={{ fontFamily: "Inter, sans-serif" }}>Équipement Actif</InputLabel>
            <StyledSelect
              name="equipement_active"
              value={formData.equipement_active}
              onChange={handleInputChange}
            >
              <MenuItem value={0}>Non</MenuItem>
              <MenuItem value={1}>Oui</MenuItem>
            </StyledSelect>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel sx={{ fontFamily: "Inter, sans-serif" }}>Statut (État)</InputLabel>
            <StyledSelect
              name="etat"
              value={formData.etat}
              onChange={handleInputChange}
            >
              <MenuItem value={0}>Désactivé</MenuItem>
              <MenuItem value={1}>Activé</MenuItem>
            </StyledSelect>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel sx={{ fontFamily: "Inter, sans-serif" }}>Actif</InputLabel>
            <StyledSelect
              name="active"
              value={formData.active}
              onChange={handleInputChange}
            >
              <MenuItem value={0}>Non</MenuItem>
              <MenuItem value={1}>Oui</MenuItem>
            </StyledSelect>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <StyledCancelButton
            onClick={() => setOpenCreateDialog(false)}
            disabled={loading}
            startIcon={<Cancel sx={{ color: '#fff' }} />}
          >
            Annuler
          </StyledCancelButton>
          <StyledButton
            onClick={handleCreateSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <Save sx={{ color: '#fff' }} />}
          >
            Créer
          </StyledButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: "Inter, sans-serif" }}>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: "Inter, sans-serif" }}>
            Êtes-vous sûr de vouloir supprimer ce livreur ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <StyledCancelButton
            onClick={() => setOpenDeleteDialog(false)}
            disabled={loading}
            startIcon={<Cancel sx={{ color: '#fff' }} />}
          >
            Annuler
          </StyledCancelButton>
          <StyledDeleteButton
            onClick={handleDelete}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <Delete sx={{ color: '#fff' }} />}
          >
            Supprimer
          </StyledDeleteButton>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: '100%', borderRadius: 12, boxShadow: '0 4px 12px rgba(15, 23, 42, 0.1)', fontFamily: "Inter, sans-serif" }}
        >
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: '100%', borderRadius: 12, boxShadow: '0 4px 12px rgba(15, 23, 42, 0.1)', fontFamily: "Inter, sans-serif" }}
        >
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default Livreurs;